import { DataGrid, GridColDef, GridRenderCellParams, getGridStringOperators } from '@mui/x-data-grid';

import * as React from "react";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "../../assets/styles/_notification_log.scss";
import { getCameraByCameraId, getFurnaceByFurnaceId } from "../../configuration";
import { setCurrentCamera, setCurrentCameraId, setSelectedTimestamp } from "../../slices/cameraSlice";
import { setCameraNameForCameraStatus, setFurnaceNameForCameraStatus } from "../../slices/cameraStatusSlice";
import { setFurnaceId, setFurnaceOpMode } from "../../slices/furnaceSlice";
import { setCurrentFurnaceId } from "../../slices/scenarioSimulatorSlice";
import { REACT_APP_APIM_URL, TI_API_CALL_INTERVAL } from "../../utilities/GlobalConstants";
import { convertDateTimeBasedOnConfig, extractCoilNameFromSlice, getEventDetails, getToken } from "../../utilities/helperFunctions";
import { setCurrentCoil } from '../../slices/coilSlice';
import { resetNotificaitonFilterLog, setPagination, resetPagination, setSortModel, resetSortModel, resetNotificationHistory, setNotificationHistory, setApiResultStatus, setIsFilterOpen} from '../../slices/notificationLogSlice';
import { RootState } from '../../reducers/rootReducer';
import FilterHeader from './filterHeader';
import { Box, IconButton, Tooltip } from '@material-ui/core';
import {Refresh, Clear} from '@material-ui/icons';

export const NOTIFICATION_LOG_DASHBOARD = "_NOTIFICATION_LOG";
export const ACTIVE_EVENTS_LOG = "_ACTIVE_EVENTS";

export interface NotificationProps {
  pageKey: string
}
const initalPageModel = {
  page: '1',
  pageSize: '20'
}
const NotificationLog: React.SFC<NotificationProps> = ({pageKey = ''}) => {
  const {filters, notificationHistory, apiResultStatus, pagination, sortModel, isFilterOpen} = useSelector((state: RootState) => state.notificationState)
  const oidcAuth = useAuth();
  const [auth, setAuth] = useState(oidcAuth);
  const dispatch = useDispatch()

  const renderColumns = () => {
    const columns: GridColDef[] = [
      {
        field: 'event_type', headerName: 'EVENT TYPE', width: 150,
        renderCell: (params) => <Link onClick={() => onRedirect(params)} to={navigateTo(params.row['event_type'])}>{params.row['event_type']}</Link>
      },
      { field: 'rule_name', headerName: 'NAME OF EVENT', width: isFilterOpen ? 300 : 220,  renderHeader :(params) => {
        return (
          <FilterHeader headerName = {params.colDef.headerName}  isSortingEnabled fieldName={params.field} />
        )
      }},
      {
        field: 'timestamp', headerName: 'DATE/TIMESTAMP', width: isFilterOpen ? 350 : 230, type: 'dateTime',  valueFormatter: (params) => convertDateTimeBasedOnConfig(params), renderHeader :(params) => {
          return (
            <FilterHeader headerName = {params.colDef.headerName} isSortingEnabled fieldName={params.field}/>
          )
        }
      },
      { field: 'furnace_id', headerName: 'FURNACE', valueGetter: (value, row) => getFurnaceByFurnaceId(row['furnace_id']), minWidth: 100, width: isFilterOpen ? 300 : 180,renderHeader :(params) => {
        return (
          <FilterHeader headerName = {params.colDef.headerName}  fieldName={params.field}/>
        )
      }},
      { field: 'camera_id', headerName: 'CAMERA', width: isFilterOpen ? 300 : 180, filterOperators: getGridStringOperators().filter((operartor) => operartor.value === "contains"),  renderHeader :(params) => {
        return (
          <FilterHeader headerName = {params.colDef.headerName} isSortingEnabled  fieldName={params.field}/>
        )
      } },
      { field: 'operating_mode', headerName: 'OPERATING MODE', width: 150,  sortable: false },
      { field: 'equipment_level', headerName: 'EQUIPMENT LEVEL', width: 150,  sortable: false },
      {
        field: 'rule_severity', headerName: 'SEVERITY', width:170, 
        renderCell: (params: GridRenderCellParams) => {
          let color = 'medium-severity'
          if (params.value === 'High') {
            color = 'high-severity'
          }
          return (
            <div className={color}>
              {params.value}
            </div>
          );
        },
        renderHeader :(params) => {
          return (
            <FilterHeader headerName = {params.colDef.headerName} isSortingEnabled  fieldName={params.field} isFilteringEnabled= {false}/>
          )
        }
      },
      { field: 'rule_category', headerName: 'CATEGORY', width: 150,  sortable: false},
      { field: 'rule_description', headerName: 'EVENT TEXT',   valueGetter: (value, row) => getEventDetails(row['rule_id'], getFurnaceByFurnaceId(row['furnace_id']), row['coilgroup_name_max'], row['coilgroup_name_min'], row['camera_id'], row['camera_temperature'], row['coilgroup_name'],row['camera_id_min'], row['camera_id_max'], { sliceName: row['sliceName'], neighbourSliceName: row['neighbourSliceName']}) , width: 180, },
      { field: 'threshold', headerName: 'THRESHOLD (F°)', width: 130,  sortable: false },
      { field: 'value', headerName: pageKey === 'activeEvent' ? 'CURRENT VALUE (F°)' : 'VALUE (F°)', width: 120,  sortable: false}
    ];
  
    if (pageKey === 'activeEvent') {
      columns.splice(2, 0, { 
        field: 'start_of_event', 
        headerName: 'START OF EVENT', 
        width: 150, 
        type: 'dateTime',
        valueFormatter: (params) => convertDateTimeBasedOnConfig(params) 
      });
    }
  
    return columns;
  }

  let columns = renderColumns();
    const getNotificationLog = async () => {
        const queryParams = new URLSearchParams({
          page: pagination.page,
          pageSize: pagination.pageSize,
        });
        const filtersFromSession = JSON.parse(sessionStorage.getItem('notificationLogFilters')!)
        const sortingModelFromSession = JSON.parse(sessionStorage.getItem('notificationLogSortModel')!) 
        const notifiactionLogPayload = {
          filter: filtersFromSession ?? [],
          sort: sortingModelFromSession ?? []
        }
        const accessToken = await getToken(auth);
        const url = `${REACT_APP_APIM_URL}/thermalimage/${pageKey  === 'activeEvent'? 'getNotificationEvents' : 'getNotificationLog' }/history?${queryParams.toString()}`;
        dispatch(setApiResultStatus(false)) //for loading
        fetch(url,
          {
            method: "POST",
            headers: {
              Accept: "*/*",
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
              "Access-Control-Allow-Origin": "*",
            },
            body: JSON.stringify(notifiactionLogPayload) 
          })
          .then((res) => res.json())
          .then(async (data) => {
            const responseObj = {
              rows: data.rows,
              totalRowCount: data.totalRowCount
            }
            dispatch(setNotificationHistory(responseObj))
            dispatch(setApiResultStatus(true)) //for loading
          })
          .catch((error) => {
            dispatch(setNotificationHistory({rows: [], totalRowCount: 0}))
            dispatch(setApiResultStatus(true)) //for loading
            console.log(error);
          });
      }


  const navigateTo = (event_type) => {
    return event_type === 'Camera Event' ? `/camera-status` : event_type === 'TMT Deviation' ? '/thermalimage' : '/multi-camera'
  }

  const onRedirect = (params) => {
    console.log(params,'check this params');
    let event_type = params.row['event_type'];
    let furnace_id = params.row['furnace_id'];
    let op_mode = params.row['operating_mode'];
    let cameraId = params.row['camera_id'];
    // fix if camera id has multiple cameras in it
    //take the first cameara id
    if (cameraId.includes('-')) {
      cameraId = (cameraId.split('-')[0]).trim();
    }
    let selectedTimeStamp;
    if (pageKey === 'activeEvent') {
      selectedTimeStamp = params.row['start_of_event'];
    }else{
      selectedTimeStamp = params.row['timestamp'];
    }
    const coilNumber = extractCoilNameFromSlice(params?.row['sliceName']);
    
    const cameraName = getCameraByCameraId(cameraId);
    const furnaceName = getFurnaceByFurnaceId(parseInt(furnace_id));

    dispatch(setFurnaceId(furnace_id));
    dispatch(setCurrentFurnaceId(furnace_id));

    if(event_type === 'TMT Deviation') {
      dispatch(setCurrentCameraId(cameraId));
      if(selectedTimeStamp) dispatch(setSelectedTimestamp(selectedTimeStamp));
      dispatch(setCurrentCamera(cameraName));
      dispatch(setCurrentCoil(coilNumber));
    } else {
      dispatch(setFurnaceOpMode(op_mode))
      dispatch(setFurnaceNameForCameraStatus(furnaceName));
      dispatch(setCameraNameForCameraStatus(cameraName));
      dispatch(setCurrentCamera(cameraName));
    }
  }

  const handlePagiantion = (data) => {
    dispatch(setPagination({
      page: data.page + 1,
      pageSize: data.pageSize
    }))
  }
  
  const resetNotificationLogs = () => {
    dispatch(resetPagination());
    dispatch(resetNotificaitonFilterLog());
    dispatch(resetSortModel());
    dispatch(resetNotificationHistory());
    sessionStorage.setItem('notificationLogFilters', JSON.stringify([]))
    sessionStorage.setItem('notificationLogSortModel', JSON.stringify([]))
    dispatch(setIsFilterOpen(false))
   }

  useEffect(() => {
      getNotificationLog();
  }, [filters, sortModel, pagination.page, pagination.pageSize, pageKey])


  useEffect(() => {
    setAuth(oidcAuth);  
  }, [oidcAuth]);

  useEffect(() => {
    //reset filters on unmount
    return () => {
      resetNotificationLogs();
    }
  }, [pageKey]);
  useEffect(() => {
    //clear session when refreshed
    window.onbeforeunload = function() {
      resetNotificationLogs()
  };

  return () => {
      window.onbeforeunload = null;
  };
  }, []);
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: 5, gridColumnGap: 2, marginTop: 12 }}>
        <Tooltip title={<span className='TooltipTitle'>Clear all filters</span>} className='materialUITooltip' placement='top'>
          <IconButton onClick={() => dispatch(resetNotificaitonFilterLog())}>
            <Refresh fontSize='large' />
          </IconButton>
        </Tooltip>
        <Tooltip title={<span className='TooltipTitle'>Clear all sortings</span>} className='materialUITooltip' placement='top'>
          <IconButton onClick={() => dispatch(resetSortModel())}>
            <Clear fontSize='large' />
          </IconButton>
        </Tooltip>
      </Box>

      <DataGrid
        className="notification-log"
        columns={columns}
        key={pageKey}
        rows={notificationHistory.rows}
        rowCount={notificationHistory.totalRowCount}
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        loading={!apiResultStatus}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: Number(initalPageModel.pageSize),
              page: Number(initalPageModel.page) - 1
            },
          }
        }}
        disableColumnFilter
        disableColumnSorting
        columnHeaderHeight={isFilterOpen ? 60 : 45}
        onPaginationModelChange={handlePagiantion}
        pageSizeOptions={[20, 60, 100]}
      />
    </>
  );
};

export default (NotificationLog);
